import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  Injector,
  runInInjectionContext,
  output,
  signal,
  untracked,
  ViewChild,
  OnDestroy,
} from "@angular/core";
import { Router } from "@angular/router";

import { NavItemsMobile, GlossRoutes, NavItem } from "../../../models/enum/routes.enum";
import { MoreTool } from "../more-overview/more-overview.component";
import { EditNavigationResult } from "../edit-navigation/edit-navigation.component";
import { Menu } from "../menu";
import { StateService } from "@bitwarden/web-vault/app/core";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";
import { ScopeValue } from "@bitwarden/web-vault/app/models/data/role-access.data";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MonthlySummaryService } from "@bitwarden/web-vault/app/services/monthly-summary/monthly-summary.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { PluginConfigService } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";

import { NAV_LAYOUT_CACHE_KEY } from "./nav-layout-cache";

const WEALTH_COMPARISON_TOOL_KEY = "wealth-comparison";
/** How long the bar waits for the preferences (with no cached layout) before showing the default. */
const DEFAULT_LAYOUT_FALLBACK_MS = 8000;

/** The user's tab bar customization: the saved pinnedNavOrder and pinnedCount preferences. */
interface NavLayout {
  order?: string[];
  count?: number;
}

@Component({
  selector: "app-menu",
  templateUrl: "./app-menu.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppMenuComponent extends Menu implements AfterViewInit, OnDestroy {
  openHelpBoxEvent = output<void>();
  menuHeightEvent = output<number>();
  private stateService = inject(StateService);
  private userStoreService = inject(UserStoreService);
  private roleAccessService = inject(RoleAccessService);
  private router = inject(Router);
  private injector = inject(Injector);
  private log = inject(LogService);
  private monthlySummaryService = inject(MonthlySummaryService);
  private pluginConfigService = inject(PluginConfigService);
  private confirmationDialog = inject(ConfirmationDialogService);

  @ViewChild("menuDiv") menuDiv!: ElementRef<HTMLDivElement>;

  protected readonly process = process;
  protected readonly GlossRoutes = GlossRoutes;

  menuItems = signal<NavItem[]>([]);
  // Expose menuItems to parent class for itemSignalStates computed
  protected menuItemsSignal = this.menuItems;
  hasOverflowItems = false;
  /** How many leading items of menuItems() are pinned to the primary tab bar (3-4, defaults to 4). */
  pinnedCount = 4;
  showMoreOverview = signal(false);
  showEditNavigation = signal(false);
  protected eligibleItems: NavItem[] = [];
  /** Tools shown at the top of the More overview. Recurring/Wealth are beta-only. */
  protected moreTools: MoreTool[] = [];
  private dialogFactory?: DialogFactory;
  /** Keys the localStorage copy of the nav layout, which is shown until the preferences load. */
  private userId: string | null = null;
  private destroyed = false;
  /** Set once the preferences failed to arrive in time, so the bar falls back to the default layout. */
  private useDefaultLayout = false;
  private defaultLayoutTimer?: ReturnType<typeof setTimeout>;
  private heightPublishPending = false;
  private lastPublishedHeight: number | null = null;

  constructor() {
    super();
    // Initialize selected key
    this.selectedKey.set(GlossRoutes.Views.toString());
    this.stateService.activeAccount$.pipe(takeUntilDestroyed()).subscribe(async () => {
      // The account changed: until getUserId() resolves below, the previous user's id must not
      // key the layout cache (it would write/read the wrong user's layout).
      this.userId = null;
      const roleScope = await this.stateService.getRoleScope();
      // Do NOT remove (#5046): this duplicates RoleAccessService.initialise (same emission), but
      // the write has to land right before setMenuItems() reads the scope. The two handlers don't
      // await each other, and a first pass over RoleAccessData's initial empty scope filters out
      // every item, with nothing to re-trigger it.
      this.roleAccessService.setRoleScope(roleScope);
      try {
        this.userId = (await this.stateService.getUserId()) ?? null;
      } catch {
        // No user id only means no layout cache; the nav must still render.
        this.userId = null;
      }
      this.setMenuItems();
    });
    // The saved nav order lives in the user's preferences, which load asynchronously during
    // bootstrap - usually AFTER the activeAccount$ handler above has already run on a refresh or
    // fresh login. Without re-deriving when they arrive, the bar keeps the default
    // Home/Rewards/Accounts/Goals layout and the saved customization is never applied.
    effect(() => {
      this.userStoreService.preferences.preferenceView();
      // Nothing to reorder until setMenuItems() has populated the eligible items; it reads the
      // preferences itself when it does.
      untracked(() => {
        if (this.eligibleItems.length > 0) {
          this.applySavedPreferences();
        }
      });
    });
  }

  private setMenuItems() {
    // Mirrors RoleScopeDirective's own check, so More/Edit Navigation (which render
    // pinnedItems/otherItems directly, without *appRoleScope gating each row) can't show an
    // item the user lacks scope for, even though the primary pill already hides it. This also
    // drops BETA_ACCESS-scoped items for non-beta users (hasScopeAccess only passes them when
    // the scope array holds BETA_ACCESS).
    this.eligibleItems = NavItemsMobile.filter((item) => this.hasScopeAccess(item.roleScope));
    const isBetaUser = this.roleAccessService.getRoleAccess().getScope().includes("BETA_ACCESS");
    this.moreTools = [
      { key: GlossRoutes.GlossGrocery, icon: "grocery", label: "groceryPriceComparison" },
      ...(isBetaUser
        ? [
            {
              key: GlossRoutes.RecurringDetector,
              icon: "refresh-line",
              label: "recurringDetection",
            },
            { key: WEALTH_COMPARISON_TOOL_KEY, icon: "wallet", label: "wealthComparison" },
          ]
        : []),
    ];
    this.applySavedPreferences();
  }

  /** Re-derives menuItems/pinnedCount/hasOverflowItems from eligibleItems + the saved nav preferences. */
  private applySavedPreferences(): void {
    const preference = this.userStoreService.preferences.preferenceView();
    if (preference) {
      this.clearDefaultLayoutTimer();
      this.useDefaultLayout = false;
      this.cacheLayout({ order: preference.pinnedNavOrder, count: preference.pinnedCount });
    } else if (!this.readCachedLayout() && !this.useDefaultLayout) {
      // Preferences not loaded yet (a refresh/login, or after logout) and nothing cached for this
      // user: the saved layout is unknown, and the default order would flash the stock tabs
      // (e.g. Goals) the user has since replaced. The effect in the constructor re-runs on load.
      // If they never arrive (load failed), fall back to the default layout so the user is not
      // left without navigation.
      this.scheduleDefaultLayoutFallback();
      this.menuItems.set([]);
      this.hasOverflowItems = false;
      this.publishMenuHeight();
      return;
    }
    this.menuItems.set(this.applySavedOrder(this.eligibleItems));
    this.pinnedCount = this.readPinnedCount();
    this.hasOverflowItems = this.menuItems().length > this.pinnedCount;
    this.publishMenuHeight();
  }

  private scheduleDefaultLayoutFallback(): void {
    if (this.defaultLayoutTimer !== undefined) {
      return;
    }
    this.defaultLayoutTimer = setTimeout(() => {
      this.defaultLayoutTimer = undefined;
      if (this.destroyed || this.userStoreService.preferences.preferenceView()) {
        return;
      }
      this.useDefaultLayout = true;
      if (this.eligibleItems.length > 0) {
        this.applySavedPreferences();
      }
    }, DEFAULT_LAYOUT_FALLBACK_MS);
  }

  private clearDefaultLayoutTimer(): void {
    if (this.defaultLayoutTimer !== undefined) {
      clearTimeout(this.defaultLayoutTimer);
      this.defaultLayoutTimer = undefined;
    }
  }

  /** Measures the bar after the next render and publishes it when it changed. Re-run whenever the
   * items change, because an empty bar (preferences not loaded, nothing cached) is shorter than a
   * full one. Calls in the same tick are coalesced and an unchanged height is not re-emitted. */
  private publishMenuHeight(): void {
    if (this.heightPublishPending) {
      return;
    }
    this.heightPublishPending = true;
    setTimeout(() => {
      this.heightPublishPending = false;
      if (this.destroyed || !this.menuDiv) {
        return;
      }
      const menuHeight = this.menuDiv.nativeElement.offsetHeight;
      if (menuHeight === this.lastPublishedHeight) {
        return;
      }
      this.lastPublishedHeight = menuHeight;
      this.menuHeightEvent.emit(menuHeight);
      document.documentElement.style.setProperty("--app-menu-height", `${menuHeight}px`);
    });
  }

  private hasScopeAccess(roleScope: ScopeValue): boolean {
    const scope = this.roleAccessService.getRoleAccess().getScope();
    return scope.includes(roleScope) || scope.includes("BETA_ACCESS");
  }

  /** Reorders `eligible` per the user's saved pinnedNavOrder preference, if any. */
  private applySavedOrder(eligible: NavItem[]): NavItem[] {
    const savedOrder = this.currentLayout()?.order;
    if (!savedOrder?.length) {
      return eligible;
    }
    const byKey = new Map(eligible.map((item) => [item.key, item]));
    const ordered = savedOrder
      .map((key) => byKey.get(key))
      .filter((item): item is NavItem => !!item);
    const orderedKeys = new Set(ordered.map((item) => item.key));
    const newlyAdded = eligible.filter((item) => !orderedKeys.has(item.key));
    return [...ordered, ...newlyAdded];
  }

  /** The user's saved pinned-tab-bar count (3-4), defaulting to 4 if never customized. */
  private readPinnedCount(): number {
    const saved = this.currentLayout()?.count;
    return saved === 3 || saved === 4 ? saved : 4;
  }

  /** The loaded preferences' layout, or this user's cached copy while they are still loading. */
  private currentLayout(): NavLayout | null {
    const preference = this.userStoreService.preferences.preferenceView();
    return preference
      ? { order: preference.pinnedNavOrder, count: preference.pinnedCount }
      : this.readCachedLayout();
  }

  private cacheLayout(layout: NavLayout): void {
    if (!this.userId) {
      return;
    }
    try {
      localStorage.setItem(NAV_LAYOUT_CACHE_KEY + this.userId, JSON.stringify(layout));
    } catch {
      // Storage unavailable/full: the bar just waits for the preferences, as it would uncached.
    }
  }

  private readCachedLayout(): NavLayout | null {
    if (!this.userId) {
      return null;
    }
    try {
      const raw = JSON.parse(localStorage.getItem(NAV_LAYOUT_CACHE_KEY + this.userId) ?? "null");
      if (!raw || typeof raw !== "object") {
        return null;
      }
      return {
        order: Array.isArray(raw.order)
          ? raw.order.filter((key: unknown) => typeof key === "string")
          : undefined,
        count: typeof raw.count === "number" ? raw.count : undefined,
      };
    } catch {
      return null;
    }
  }

  ngAfterViewInit() {
    this.publishMenuHeight();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.clearDefaultLayoutTimer();
    // Clear the variable so consumers (e.g. manage-accounts' panel offset) fall back to 0px
    // when the nav isn't rendered, instead of holding a stale height.
    document.documentElement.style.removeProperty("--app-menu-height");
  }

  selectKey(key: string) {
    if (key === "help") {
      this.openHelpBoxEvent.emit();
    } else {
      this.selectedKey.set(key);
    }
  }

  /** A pinned item's link click. A modified click (Ctrl/Cmd/Shift/Alt, or not the main button)
   * opens the link in a new tab or window, as RouterLink leaves it to the browser: this tab's route
   * doesn't change, so neither does its selection. */
  protected onPinnedItemClick(event: MouseEvent, key: string): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }
    this.selectKey(key);
  }

  isHiddenItemSelected(): boolean {
    // Check if selected key matches any item beyond pinnedCount, or any More tool (tool pages
    // aren't in the tab bar, so More is what stays highlighted on them).
    const hiddenItems = this.menuItems().slice(this.pinnedCount);
    return [...hiddenItems, ...this.moreTools].some((item) => item.key === this.selectedKey());
  }

  openMoreOverview(): void {
    this.showMoreOverview.set(true);
  }

  onOverviewItemSelected(item: NavItem): void {
    this.showMoreOverview.set(false);
    this.selectKey(item.key);
    void this.router.navigate(Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink]);
  }

  async onToolSelected(tool: MoreTool): Promise<void> {
    this.showMoreOverview.set(false);
    if (tool.key !== WEALTH_COMPARISON_TOOL_KEY) {
      // Same as onOverviewItemSelected: without this the tab bar keeps highlighting the
      // previously selected tab, since nothing else clears selectedKey for a tool page.
      this.selectKey(tool.key);
      void this.router.navigate(["/", tool.key]);
      return;
    }
    try {
      // Same as the plugin details dialog: mark the plugin installed before opening, so the
      // Plugins page doesn't show a plugin the user is actively using as not installed.
      const activated = await this.pluginConfigService.ensureWealthComparisonPluginActivated();
      if (!activated) {
        throw new Error("Failed to activate wealth comparison plugin");
      }
      // DialogFactory calls inject() in its field initializers, so it must be built inside an
      // injection context — a plain `new` from a click handler throws NG0203. Cached so reopening
      // reuses the instance (and its dialogRef) instead of stacking dialogs.
      this.dialogFactory ??= runInInjectionContext(this.injector, () => new DialogFactory());
      await this.dialogFactory.openWealthComparisonDialog();
    } catch (error) {
      this.log.error(
        "Failed to open wealth comparison from More: " +
          (error instanceof Error ? error.message : String(error)),
      );
      this.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  onOverviewClosed(): void {
    this.showMoreOverview.set(false);
  }

  onMonthlySummarySelected(): void {
    this.showMoreOverview.set(false);
    void this.monthlySummaryService.open();
  }

  onEditNavigationRequested(): void {
    this.showMoreOverview.set(false);
    this.showEditNavigation.set(true);
  }

  onEditNavigationCancelled(): void {
    this.showEditNavigation.set(false);
  }

  async onEditNavigationSaved(result: EditNavigationResult): Promise<void> {
    const preference = this.userStoreService.preferences.preferenceView()?.clone();
    if (!preference) {
      // Preferences not loaded (cached layout is only a display copy): nothing to save onto, and
      // leaving the dialog open would make Save look like it hangs.
      this.showEditNavigation.set(false);
      return;
    }
    preference.pinnedNavOrder = result.order.map((item) => item.key);
    preference.pinnedCount = result.pinnedCount;
    await this.userStoreService.preferences.save(preference);
    this.applySavedPreferences();
    this.showEditNavigation.set(false);
  }
}
